import defaultTo from 'lodash/defaultTo';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Avatar from '@atlaskit/avatar';
import { Field } from '@atlaskit/form';
import Select, { ValueType, components } from '@atlaskit/select';
import { Flex, Text, Box } from '@atlaskit/primitives';
import Tag from '@atlaskit/tag';
import { BillingAdmin } from '@css-xp/ppc-graphtypes';

type BillingAdminSelectItem = BillingAdmin & {
  label: React.ReactNode;
  value: string;
};

interface AddContactMultiSelectFieldsProps {
  currentUserList?: Array<string>;
  fieldType?: string;
  billingAdmins?: BillingAdmin[];
  billingAdminsLoading?: boolean;
  setBillingAdmins?: Dispatch<SetStateAction<BillingAdmin[]>>;
  emailFieldText?: string;
  selectedUserList?: BillingAdminSelectItem[];
  isMulti?: boolean;
}

type BillingAdminSelectOptionProps = {
  billingAdmin: BillingAdmin | null | undefined;
};

export const BillingAdminSelectOption = ({ billingAdmin }: BillingAdminSelectOptionProps) => {
  return (
    <Box paddingInline="space.050" paddingBlock="space.075">
      <Flex alignItems="center" key={`select-option-${billingAdmin?.atlassianAccountId}`} gap="space.150">
        <Avatar src={billingAdmin?.picture as string} size="medium" />
        <Text>{billingAdmin?.fullName}</Text>
      </Flex>
    </Box>
  );
};

const MultiValueContainer = (props: any) => {
  return (
    <div>
      <components.MultiValueContainer {...props}>
        <Tag
          text={props.data.fullName}
          elemBefore={<Avatar borderColor="transparent" size="xsmall" src={props.data.picture} />}
          appearance="rounded"
          onAfterRemoveAction={(removedAdmin: string) => props.selectProps?.onRemoveAdmin(removedAdmin)}
          testId={`billing-admin-tag-${props.data.atlassianAccountId}`}
        />
      </components.MultiValueContainer>
    </div>
  );
};

export const AddContactMultiSelectField = ({
  currentUserList,
  emailFieldText,
  fieldType,
  billingAdmins,
  setBillingAdmins,
  billingAdminsLoading,
  selectedUserList,
  isMulti,
}: AddContactMultiSelectFieldsProps) => {
  const [userList, setUserList] = useState<BillingAdminSelectItem[]>(selectedUserList ? selectedUserList : []);

  const fieldName = fieldType ?? 'billing-contact';

  const availableBillingAdminOptions = defaultTo(billingAdmins, [])
    ?.filter((billingAdmin: BillingAdmin | null | undefined) => {
      return !currentUserList?.includes(billingAdmin?.atlassianAccountId as string);
    })
    ?.map((billingAdmin: BillingAdmin | null | undefined) => {
      return {
        label: <BillingAdminSelectOption billingAdmin={billingAdmin} />,
        value: billingAdmin?.fullName,
        email: defaultTo(billingAdmin?.email, ''),
        atlassianAccountId: defaultTo(billingAdmin?.atlassianAccountId, ''),
        fullName: defaultTo(billingAdmin?.fullName, ''),
        picture: defaultTo(billingAdmin?.picture, ''),
      };
    }) as BillingAdminSelectItem[];

  useEffect(() => {
    setBillingAdmins?.(userList);
  }, [userList, setBillingAdmins]);

  return (
    <div>
      <Field<ValueType<BillingAdminSelectItem[]>, HTMLSelectElement> name={`${fieldName}-select`} isRequired>
        {({ fieldProps }) => (
          <>
            <Select<BillingAdminSelectItem[]>
              {...fieldProps}
              options={availableBillingAdminOptions as any}
              testId="billing-admin-multi-select-field"
              apperance="default"
              enableAnimation={false} // this is for remove animation, it looks weird hence disabled
              classNamePrefix="billing-admin-select"
              onChange={(selectedItem) => {
                setUserList(selectedItem as BillingAdminSelectItem[]);
                fieldProps.onChange(selectedItem);
              }}
              isLoading={billingAdminsLoading}
              isSearchable
              placeholder={emailFieldText}
              closeMenuOnSelect
              // @ts-ignore
              isMulti={isMulti ?? true}
              name={fieldName}
              escapeClearsValue={false} // since escape closes the sidebar
              hideSelectedOptions
              onRemoveAdmin={(removedAdmin: string) => {
                const newBillingAdmins = userList?.filter((admin) => admin.fullName !== removedAdmin);
                setUserList?.(newBillingAdmins);
                fieldProps.onChange(newBillingAdmins as any);
              }}
              components={{
                MultiValueContainer,
              }}
              styles={{
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: 'transparent',
                }),
              }}
              value={userList}
            />
          </>
        )}
      </Field>
    </div>
  );
};
